import React from 'react';
import { Box, Typography } from '@mui/material';
import { Diamond, HubButton, FONT_DISPLAY, FONT_BODY } from '../clients/hub/designSystem';
import { HEAD } from '../v2/kit';
import { Diamond as V2Diamond } from '../v2/Diamond';
import { Kicker as V2Kicker } from '../v2/Kicker';

interface EmptyAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'ghost' | 'danger';
}

/**
 * HubEmptyState (CD-03) — the one empty-state grammar: diamond → Kicker voice
 * → one honest sentence (what's missing + how it fills) → at most one (or two)
 * HubButton. Search-aware: pass `query` and the copy echoes it. Three layouts:
 * `card` (bordered flat card), `inline` (compact, no border), `page` (large,
 * centered). Never an emoji, never a blank; says what's missing and how it fills.
 * Design System 2.0 (the published HubEmptyState page; step 5 wave 1a): an
 * indigo diamond, the kicker in its one voice (11 / 800 / .12em, indigo), a 20
 * Inter Tight 800 title, one 13.5 sentence, the small 32 HubButton; the card on
 * the 6px panel corner.
 */
export interface HubEmptyStateClassicProps {
  kicker?: string;
  title?: string;
  body?: React.ReactNode;
  action?: EmptyAction;
  secondaryAction?: EmptyAction;
  /** Search-aware: when a non-empty query is passed, the copy echoes it. */
  query?: string;
  variant?: 'card' | 'inline' | 'page';
}

/**
 * `variant="stacked"` — the Portal V2 empty state (design kit.jsx:283-293
 * `Empty`), opt-in; the three classic variants render byte-identically. A 10px
 * diamond in the `tone` (indigo by default), a kicker in the tone, a 17/800
 * title, one honest 13.5 sentence in ink-soft (max 440) and at most one action
 * (a node — never the page header's primary), stacked 11px apart and centred,
 * padded 64/24 (`compact`: 32/20) and rising in with `v2-fadeUp`. Render it
 * inside a `V2Root`.
 */
export interface HubEmptyStateStackedProps {
  variant: 'stacked';
  kicker?: React.ReactNode;
  title?: React.ReactNode;
  /** The one sentence (the design's children). */
  body?: React.ReactNode;
  /** At most one action, e.g. a V2 `Btn`. */
  action?: React.ReactNode;
  tone?: string;
  compact?: boolean;
  style?: React.CSSProperties;
}

export type HubEmptyStateProps = HubEmptyStateClassicProps | HubEmptyStateStackedProps;

const HubEmptyStateStacked: React.FC<HubEmptyStateStackedProps> = ({ kicker, title, body, action, tone = 'var(--indigo)', compact, style }) => (
  <div className="v2-fadeUp" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: compact ? '32px 20px' : '64px 24px', gap: 11, ...style }}>
    <V2Diamond size={10} color={tone} />
    {kicker && <V2Kicker tone={tone}>{kicker}</V2Kicker>}
    {title && <div style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800, color: 'var(--ink)', letterSpacing: '-.015em' }}>{title}</div>}
    <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.6, maxWidth: 440, textWrap: 'pretty' } as React.CSSProperties}>{body}</div>
    {action && <div style={{ marginTop: 4 }}>{action}</div>}
  </div>
);

/** HubEmptyState — dispatches on `variant` (stacked is its own component type). */
export const HubEmptyState: React.FC<HubEmptyStateProps> = (props) => (
  props.variant === 'stacked' ? <HubEmptyStateStacked {...props} /> : <HubEmptyStateClassic {...props} />
);

const HubEmptyStateClassic: React.FC<HubEmptyStateClassicProps> = ({ kicker, title, body, action, secondaryAction, query, variant = 'card' }) => {
  const searching = !!query && query.trim() !== '';
  const resolvedKicker = kicker ?? (searching ? 'No matches' : 'Nothing here yet');
  const resolvedBody = body ?? (searching ? (
    <>No results for <Box component="span" sx={{ color: 'var(--ink)', fontWeight: 600 }}>&ldquo;{query}&rdquo;</Box>. Try a different search.</>
  ) : undefined);

  const pad = variant === 'page' ? { py: 8, px: 4 } : variant === 'inline' ? { py: 2.5, px: 2 } : { py: 5, px: 3 };

  return (
    <Box
      sx={{
        textAlign: 'center', ...pad,
        ...(variant === 'card' ? { border: '1px solid var(--rule)', borderRadius: '6px', bgcolor: 'var(--paper)' } : null),
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
        <Diamond color="var(--indigo)" size={6} filled outline="none" />
        <Typography
          component="div"
          sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', fontSize: 11, color: 'var(--indigo)' }}
        >
          {resolvedKicker}
        </Typography>
      </Box>
      {title && (
        <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, letterSpacing: '-.015em', lineHeight: 1.2, color: 'var(--ink)' }}>
          {title}
        </Typography>
      )}
      {resolvedBody && (
        <Typography component="div" sx={{ fontFamily: FONT_BODY, fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.55, maxWidth: 440 }}>
          {resolvedBody}
        </Typography>
      )}
      {(action || secondaryAction) && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
          {secondaryAction && (
            <HubButton variant={secondaryAction.variant ?? 'ghost'} onClick={secondaryAction.onClick}>{secondaryAction.label}</HubButton>
          )}
          {action && (
            <HubButton variant={action.variant ?? 'primary'} onClick={action.onClick}>{action.label}</HubButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HubEmptyState;
